import { apiClient } from './api.client';
import { API_CONFIG } from '../config/api.config';
import { 
  ParkingLot, 
  ParkingSpot, 
  ParkingLotSummary, 
  ParkingSpotStatus, 
  ParkingSpotType,
  ApiResponse 
} from '../types';

export interface ParkingLotFilters {
  location_id?: number;
}

export interface AvailableSpotsFilters {
  spot_type?: ParkingSpotType;
}

export interface VehicleLocation {
  found: boolean;
  spot?: ParkingSpot;
  lot?: ParkingLot;
}

// New types for parking API responses
export interface ParkSpotRequest {
  vehicle_plate?: string;
  trailer_plate?: string;
  vehicle_type: 'car' | 'motorcycle' | 'truck' | 'trailer';
  driver_name?: string;
  notes?: string;
}

export interface ParkSpotResponse {
  success: boolean;
  message: string;
  spots: ParkingSpot[];
  session_id: number;
  entry_time: string;
}

export interface ReleaseSpotResponse {
  success: boolean;
  message: string;
  spots: ParkingSpot[];
  vehicle_plate?: string;
  vehicle_type?: string;
  driver_name?: string;
  session_id: number;
  duration_minutes: number;
  entry_time: string;
  exit_time: string;
}

export interface SpotDetails extends ParkingSpot {
  occupied_since?: string;
  current_session_id?: number;
  current_vehicle_plate?: string; // Backend field name
}

// Multi-spot occupy request - frontend selects spots
export interface MultiSpotOccupyRequest {
  vehicle_plate: string;
  trailer_plate?: string;        // Optional - only for truck with trailer
  vehicle_type: 'car' | 'truck' | 'trailer';
  driver_name?: string;
  spot_ids: number[];            // Array of spot IDs to occupy
}

// Release just needs spot IDs - plates are read from spots
export interface MultiSpotReleaseRequest {
  spot_ids: number[];
}

class ParkingService {
  /**
   * Get all parking lots for a location
   */
  async getLots(locationId: number): Promise<ApiResponse<ParkingLot[]>> {
    return apiClient.get<ApiResponse<ParkingLot[]>>(
      API_CONFIG.ENDPOINTS.PARKING_LOTS,
      { location_id: locationId }
    );
  }

  /**
   * Get all spots in a parking lot
   */
  async getSpots(lotId: number): Promise<ApiResponse<ParkingSpot[]>> {
    return apiClient.get<ApiResponse<ParkingSpot[]>>(
      `${API_CONFIG.ENDPOINTS.PARKING_LOTS}/${lotId}/spots`
    );
  }

  /**
   * Get a single spot's details
   */
  async getSpotDetails(spotId: number): Promise<SpotDetails> {
    return apiClient.get<SpotDetails>(
      `${API_CONFIG.ENDPOINTS.PARKING_SPOTS}/${spotId}`
    );
  }

  /**
   * Get available spots in a parking lot, optionally filtered by spot type
   */
  async getAvailableSpots(lotId: number, spotType?: ParkingSpotType): Promise<ApiResponse<ParkingSpot[]>> {
    const params: Record<string, string> = {};
    if (spotType) {
      params.spot_type = spotType;
    }
    return apiClient.get<ApiResponse<ParkingSpot[]>>(
      `${API_CONFIG.ENDPOINTS.PARKING_LOTS}/${lotId}/spots/available`,
      params
    );
  }

  /**
   * Get parking lot summary (availability counts by type)
   */
  async getSummary(lotId: number): Promise<ParkingLotSummary> {
    return apiClient.get<ParkingLotSummary>(
      `${API_CONFIG.ENDPOINTS.PARKING_LOTS}/${lotId}/summary`
    );
  }

  /**
   * Find where a vehicle is parked by license plate
   */
  async findVehicleSpot(lotId: number, licensePlate: string): Promise<VehicleLocation> {
    return apiClient.get<VehicleLocation>(
      `${API_CONFIG.ENDPOINTS.PARKING_LOTS}/${lotId}/find/${encodeURIComponent(licensePlate)}`
    );
  }

  /**
   * Find a vehicle across all lots in a location
   */
  async findVehicleInLocation(locationId: number, licensePlate: string): Promise<VehicleLocation> {
    return apiClient.get<VehicleLocation>(
      `${API_CONFIG.ENDPOINTS.PARKING_LOTS}/find`,
      { location_id: locationId, plate: licensePlate }
    );
  }

  // ============================================================
  // Single Spot Operations (Cars, Motorcycles)
  // ============================================================

  /**
   * Park a vehicle in a single spot
   * POST /spots/{spot_id}/park
   */
  async parkInSpot(spotId: number, data: ParkSpotRequest): Promise<ParkSpotResponse> {
    return apiClient.post<ParkSpotResponse>(
      `${API_CONFIG.ENDPOINTS.PARKING_SPOTS}/${spotId}/park`,
      data
    );
  }

  /**
   * Release a vehicle from a single spot (click-to-release)
   * POST /spots/{spot_id}/release
   * No body needed - plates are read from the spot
   */
  async releaseSpot(spotId: number): Promise<ReleaseSpotResponse> {
    return apiClient.post<ReleaseSpotResponse>(
      `${API_CONFIG.ENDPOINTS.PARKING_SPOTS}/${spotId}/release`,
      {}
    );
  }

  // ============================================================
  // Multi-Spot Operations (Trucks, Trailers)
  // ============================================================

  /**
   * Park a truck/trailer in multiple spots (2 adjacent spots)
   * POST /lots/{lot_id}/occupy
   */
  async occupyMultipleSpots(lotId: number, data: MultiSpotOccupyRequest): Promise<ParkSpotResponse> {
    return apiClient.post<ParkSpotResponse>(
      `${API_CONFIG.ENDPOINTS.PARKING_LOTS}/${lotId}/occupy`,
      data
    );
  }

  /**
   * Release a truck/trailer from multiple spots
   * POST /lots/{lot_id}/release
   */
  async releaseMultipleSpots(lotId: number, data: MultiSpotReleaseRequest): Promise<ReleaseSpotResponse> {
    return apiClient.post<ReleaseSpotResponse>(
      `${API_CONFIG.ENDPOINTS.PARKING_LOTS}/${lotId}/release`,
      data
    );
  }

  // ============================================================
  // Legacy methods (kept for backward compatibility)
  // ============================================================

  /**
   * @deprecated Use parkInSpot() instead
   * Update a parking spot's status
   */
  async updateSpotStatus(
    spotId: number, 
    status: ParkingSpotStatus, 
    currentPlate?: string
  ): Promise<ParkingSpot> {
    const data: { status: ParkingSpotStatus; current_vehicle_plate?: string } = { status };
    if (currentPlate !== undefined) {
      data.current_vehicle_plate = currentPlate;
    }
    return apiClient.put<ParkingSpot>(
      `${API_CONFIG.ENDPOINTS.PARKING_SPOTS}/${spotId}`,
      data
    );
  }

  /**
   * @deprecated Use parkInSpot() instead
   * Mark a spot as occupied with a license plate
   */
  async occupySpot(spotId: number, licensePlate: string, vehicleType?: string): Promise<ParkSpotResponse> {
    return this.parkInSpot(spotId, {
      vehicle_plate: licensePlate,
      vehicle_type: (vehicleType as 'car' | 'motorcycle' | 'truck' | 'trailer') || 'car'
    });
  }

  /**
   * @deprecated Use releaseSpot() instead
   * Free a parking spot (mark as available, clear plate)
   */
  async freeSpot(spotId: number): Promise<ReleaseSpotResponse> {
    return this.releaseSpot(spotId);
  }

  // ============================================================
  // Helper methods
  // ============================================================

  /**
   * Map vehicle type to appropriate parking spot type
   */
  getSpotTypeForVehicle(vehicleType: string): ParkingSpotType {
    const type = vehicleType?.toLowerCase() || '';
    if (type.includes('truck') || type.includes('semi')) {
      return 'truck';
    }
    if (type.includes('trailer')) {
      return 'trailer';
    }
    if (type.includes('motorcycle') || type.includes('bike')) {
      return 'motorcycle';
    }
    // Default to car for sedans, SUVs, vans, etc.
    return 'car';
  }

  /**
   * Check if a vehicle type requires multiple spots
   */
  requiresMultipleSpots(vehicleType: string): boolean {
    const type = vehicleType?.toLowerCase() || '';
    return type.includes('truck') || type.includes('trailer') || type.includes('semi');
  }

  /**
   * Format duration for display
   */
  formatDuration(minutes: number): string {
    if (minutes < 60) {
      return `${minutes}m`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  }
}

export const parkingService = new ParkingService();
